import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { Box, Text, Button, Modal } from '@rsos/base-components';
import type { MultiInputTag } from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput';
import { ErrorComponent } from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput.styles';
import { GPX_FEATURES } from '@rsos/gainsight';
import { setConversationLanguage } from '@rsos/reduxDataStore/src/features/chats/chatsSlice';
import { TranslationLanguage } from '@rsos/reduxDataStore/src/features/chats/chatsTypes';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  expand,
  setSelectedTab,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  OUTBOUND_SMS_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { type ResolvedPreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesUtils';
import {
  useCreateConversationOutboundSMSMutation,
  useLazyGetConversationMetadataQuery,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  setSelectedConversation,
  setPendingTF911Message,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import type { SmartPhraseContext } from '@rsos/utils/SmartPhrases';
import {
  formatInternationalPhoneNumberE164,
  isValidInternationalPhoneNumber,
} from '@rsos/utils/internationalPhoneNumberUtils';
import { useAppDispatch, type RootState } from '../../../../app/rootReducer';
import {
  extractLocationRequestToken,
  trackLocationRequestClicked,
  trackLocationRequestSent,
} from '../../../../gainsightTrackings/locationRequestTrackings';
import AllMessages from '../../../PreconfiguredMessages/AllMessages/AllMessages';
import { hasRequestLocationMessage } from '../../../PreconfiguredMessages/AllMessages/utils';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import { resolveTrackedLocationRequestMessage } from '../../../PreconfiguredMessages/resolveLocationRequestMessage';
import { useEnabledPreconfiguredMessages } from '../../../PreconfiguredMessages/useEnabledPreconfiguredMessages';
import RequestLocation from '../../../RequestLocation';
import { TF911InitiationTranslationMenu } from '../../CallChats/TF911ChatBox/TF911InitiationTranslationMenu';
import {
  trackSendOutboundSMSClick,
  trackStartTF911Response,
} from '../../CallChats/textFrom911Trackings';
import {
  INVALID_NUMBER,
  MSG_REQUEST_ERROR,
  INVALID_NUMBER_ERROR,
  MESSAGE_FAILED_TO_SEND,
  NUMBER_ALREADY_CALLED_911,
  EXISTING_CONVERSATION_ERROR,
} from './constants';
import {
  initialValues,
  validationSchema,
  initialErrorState,
  isBtnDisabled,
  containsCallerIdBoundUrl,
  type OutboundSMSValues,
  type ConversationMutationTuple,
} from './helpers';
import {
  Container,
  PhoneNumberContainer,
  SendMsgContainer,
  TextArea,
  TextAreaRow,
  CustomMultiEntryInput,
  TextAreaContainer,
} from './styles/OutboundSMS.styles';

const PrefixComponent = (
  <Text
    pl="6px"
    mb="5px"
    size="normal"
    color="secondaryText"
    wordBreak="keep-all"
  >
    To:
  </Text>
);

export const OutboundSMS = () => {
  const textAreaContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const [phoneNumberTags, setPhoneNumberTags] = useState<MultiInputTag[]>([]);
  const [errorMsgs, setErrorMsgs] = useState(initialErrorState);
  const [isAllMessagesOpen, setIsAllMessagesOpen] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [pendingInitiationLanguage, setPendingInitiationLanguage] =
    useState<TranslationLanguage | null>(null);
  // Resolved smart-phrase values from the selected/copied preconfigured message
  // that must not be translated. The backend filters these to whatever is still
  // present in the final message, so edits to the text field stay safe.
  const [doNotTranslate, setDoNotTranslate] = useState<string[]>([]);

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application || '',
      )?.name,
  );

  const enabledPreconfiguredMessages = useEnabledPreconfiguredMessages(
    orgName,
    { hasRequestLocation: true },
  );

  const hasRequestLocationAccess = useMemo(
    () => hasRequestLocationMessage(enabledPreconfiguredMessages),
    [enabledPreconfiguredMessages],
  );

  // The caller for this composer is the number entered here, not the globally
  // selected call. Drives which caller-ID-bound preconfigured messages show and
  // how their links resolve.
  const outboundCallerID = useMemo(
    () =>
      formatInternationalPhoneNumberE164(
        phoneNumberTags[0]?.value ?? '',
      )?.substring(1) ?? '',
    [phoneNumberTags],
  );

  const setReplicatedValue = (value: string) => {
    const container = textAreaContainerRef.current as HTMLDivElement | null;
    if (container?.dataset) {
      container.dataset.replicatedValue = value;
    }
  };

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement>,
    setFieldValue: (field: string, value: unknown) => void,
  ) => {
    const { value } = e.target;
    setFieldValue('initialOutboundMessage', value);
    setErrorMsgs(prev => ({ ...prev, [MSG_REQUEST_ERROR]: false }));
    setReplicatedValue(value);
  };

  const [createConversation, conversationResult] =
    useCreateConversationOutboundSMSMutation() as unknown as ConversationMutationTuple;
  const [getConversationMetadata] = useLazyGetConversationMetadataQuery();

  const dispatchLanguage = async (
    conversationId: string | undefined,
    language: TranslationLanguage | null,
  ) => {
    if (!language || !conversationId) return;
    await dispatch(
      setConversationLanguage({
        conversationId,
        selectedLanguageCode: language.languageCode,
        outboundTranslation: true,
      }),
    );
  };

  const handleExistingConversation = async (
    callerID: string,
    message: string,
    savedLanguage: TranslationLanguage | null,
  ) => {
    const metadata = await getConversationMetadata({
      callerID,
      orgName,
      custom: { isOutboundSMS: true },
    });
    const metadataResult = metadata as {
      isSuccess?: boolean;
      data?: { conversation_id?: string };
    };
    if (!metadataResult?.isSuccess) return;

    const conversationId = metadataResult.data?.conversation_id;
    await dispatchLanguage(conversationId, savedLanguage);
    dispatch(setSelectedCallerID(callerID));
    dispatch(setSelectedConversation(callerID));
    dispatch(setPendingTF911Message(message));
    dispatch(setSelectedTab('chat'));
    dispatch(closeCard(OUTBOUND_SMS_CARD));
    dispatch(expand());
  };

  const submitOutboundSMS = async (
    values: OutboundSMSValues,
    protectedPhrases: string[],
  ) => {
    const phoneNumberTag = values?.phoneNumberTags?.[0];
    const message = values?.initialOutboundMessage;
    const callerID =
      formatInternationalPhoneNumberE164(
        phoneNumberTag?.value ?? '',
      )?.substring(1) ?? '';

    trackSendOutboundSMSClick();

    const resp = await createConversation({
      callerID,
      message: message || undefined,
      orgName,
      languageCode: pendingInitiationLanguage?.languageCode,
      doNotTranslate: protectedPhrases,
    });

    trackStartTF911Response({
      feature: GPX_FEATURES.TF911_OUTBOUND,
      responseSuccess: !resp?.error,
      conversationId: resp?.data?.conversation_id ?? '',
    });

    if (resp?.error?.status === 409) {
      const savedLanguage = pendingInitiationLanguage;
      setPendingInitiationLanguage(null);
      await handleExistingConversation(callerID, message, savedLanguage);
      return;
    }

    if (resp?.error) {
      setErrorMsgs(prev => ({ ...prev, [MSG_REQUEST_ERROR]: true }));
      return;
    }

    // Only after the error branches, so this counts a send the backend actually
    // accepted. On a 409 the message is merely queued in redux and goes out
    // through TF911Chat, whose onMessage reports it instead.
    const sentToken = extractLocationRequestToken(message);
    if (sentToken) {
      trackLocationRequestSent({
        token: sentToken,
        pathwayType: 'TF911',
        callerID,
        conversationId: resp?.data?.conversation_id,
        isOutbound: true,
      });
    }

    await dispatchLanguage(
      resp?.data?.conversation_id,
      pendingInitiationLanguage,
    );
    setPendingInitiationLanguage(null);
    // TODO: logic on success
    dispatch(closeCard(OUTBOUND_SMS_CARD));
    dispatch(setSelectedCallerID(callerID));
    dispatch(setSelectedTab('chat'));
    dispatch(expand());
  };

  // Formik onSubmit — free-text/copied sends use whatever was last captured
  // from a preconfigured message; the backend filters to phrases present in
  // the final text, so edits stay safe.
  const handleSubmit = (values: OutboundSMSValues) =>
    submitOutboundSMS(values, doNotTranslate);

  const inputError = useMemo(() => {
    if (errorMsgs[INVALID_NUMBER_ERROR]) {
      return INVALID_NUMBER;
    }
    return null;
  }, [errorMsgs]);

  const handleCopyToChat = (
    msg: ResolvedPreconfiguredResponse,
    setFieldValue: (field: string, value: unknown) => void,
  ) => {
    setFieldValue('initialOutboundMessage', msg.body);
    setReplicatedValue(msg.body);
    setDoNotTranslate(Object.values(msg.resolvedPhrases));
    setIsAllMessagesOpen(false);
  };

  const handleRequestLocation = async (
    locationData: SmartPhraseContext,
    setFieldValue: (field: string, value: unknown) => void,
  ) => {
    setLocationError(false);

    const trackingContext = {
      pathwayType: 'TF911' as const,
      callerID: outboundCallerID,
      isOutbound: true,
    };

    trackLocationRequestClicked(trackingContext);

    const resolvedMessage = await resolveTrackedLocationRequestMessage(
      enabledPreconfiguredMessages,
      { ...locationData, callerID: outboundCallerID },
      trackingContext,
    );

    if (!resolvedMessage) {
      setLocationError(true);
      return;
    }

    setFieldValue('initialOutboundMessage', resolvedMessage.body);
    setReplicatedValue(resolvedMessage.body);
    setDoNotTranslate(Object.values(resolvedMessage.resolvedPhrases));
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors }) => {
        const hasInvalidPhoneNumber =
          !values.phoneNumberTags?.length || !!errors.phoneNumberTags;

        const handleUpdateTag = (tags: MultiInputTag[]) => {
          setPhoneNumberTags(tags);
          setErrorMsgs(prev => ({
            ...prev,
            [INVALID_NUMBER_ERROR]: false,
            [NUMBER_ALREADY_CALLED_911]: false,
            [EXISTING_CONVERSATION_ERROR]: false,
          }));
          if (containsCallerIdBoundUrl(values.initialOutboundMessage)) {
            setFieldValue('initialOutboundMessage', '');
            setReplicatedValue('');
          }
        };

        const handleSendMessage = async (
          msg: ResolvedPreconfiguredResponse,
        ) => {
          setIsAllMessagesOpen(false);
          await submitOutboundSMS(
            { ...values, initialOutboundMessage: msg.body },
            Object.values(msg.resolvedPhrases),
          );
        };

        return (
          <Form id="outbound-sms">
            <Container>
              <PhoneNumberContainer>
                <TF911InitiationTranslationMenu
                  selectedLanguage={pendingInitiationLanguage}
                  onLanguageSelect={setPendingInitiationLanguage}
                />
                <Box mt={2}>
                  <Text fontWeight="bold" size="large" color="primaryText">
                    Start Chat
                  </Text>
                  <Text size="normal" color="secondaryText">
                    Chat with a phone number that did not call 911
                  </Text>
                </Box>
                <Box width="100%" py={3}>
                  <CustomMultiEntryInput
                    value={values.phoneNumber}
                    name="phoneNumber"
                    placeholder={
                      phoneNumberTags.length ? '' : '+1 555 555 5555'
                    }
                    tags={phoneNumberTags}
                    updateTags={handleUpdateTag}
                    setFieldValue={setFieldValue}
                    validator={isValidInternationalPhoneNumber}
                    inputType="tel"
                    hiddenInputName="phoneNumberTags"
                    error={
                      inputError ??
                      (typeof errors?.phoneNumberTags === 'string'
                        ? errors.phoneNumberTags
                        : undefined)
                    }
                    disabled={!!phoneNumberTags.length}
                    prefixComponent={PrefixComponent}
                  />
                </Box>
              </PhoneNumberContainer>
              <SendMsgContainer minHeight="60px">
                <TextAreaRow>
                  <TextAreaContainer ref={textAreaContainerRef}>
                    <TextArea
                      rows={1}
                      data-name="outbound-textbox"
                      name="initialOutboundMessage"
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                        handleChange(e, setFieldValue)
                      }
                      placeholder="Message"
                      value={values.initialOutboundMessage}
                      error={
                        errors?.initialOutboundMessage ||
                        errorMsgs[MSG_REQUEST_ERROR]
                      }
                      disabled={conversationResult?.isLoading}
                    />
                    {errors?.initialOutboundMessage ? (
                      <ErrorComponent error={errors.initialOutboundMessage} />
                    ) : errorMsgs[MSG_REQUEST_ERROR] ? (
                      <ErrorComponent error={MESSAGE_FAILED_TO_SEND} />
                    ) : null}
                  </TextAreaContainer>
                  <Button
                    dataName="outbound-submit"
                    type="submit"
                    btnType="primary"
                    m={0}
                    disabled={isBtnDisabled(errors, values)}
                    isLoadingProp={conversationResult?.isLoading}
                  >
                    {values.initialOutboundMessage ? 'Send' : 'Start'}
                  </Button>
                </TextAreaRow>
                <Box horizontal align="center" justify="flex-end" width="100%">
                  {hasRequestLocationAccess && (
                    <RequestLocation
                      isOutboundSMS
                      onClick={locationData =>
                        handleRequestLocation(locationData, setFieldValue)
                      }
                      disabled={hasInvalidPhoneNumber}
                    />
                  )}
                  <SuggestedResponses
                    isOutboundSMS
                    handleViewClick={() => setIsAllMessagesOpen(true)}
                  />
                </Box>
                {locationError && (
                  <Text color="lightDanger" size="small">
                    Something went wrong, please try again
                  </Text>
                )}
              </SendMsgContainer>
            </Container>
            <Modal
              isOpened={isAllMessagesOpen}
              id="outbound-all-messages-modal"
              padding="0"
              width="448px"
              overflowY="hidden"
            >
              <AllMessages
                preconfiguredMessages={enabledPreconfiguredMessages}
                onClose={() => setIsAllMessagesOpen(false)}
                onCopyToChat={msg => handleCopyToChat(msg, setFieldValue)}
                onSendMessage={handleSendMessage}
                isOutboundSMS
                callerID={outboundCallerID}
                isSendMessageDisabled={hasInvalidPhoneNumber}
                pathwayType="TF911"
              />
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
